import { pipe } from 'fp-ts/lib/function';
import * as Option from 'fp-ts/Option';
import * as t from 'io-ts';

interface RecordWithDefaultValueC<K extends t.Mixed, V extends t.Mixed>
  extends t.Type<RecordWithDefaultValue<t.TypeOf<K>, t.TypeOf<V>>> {}

type KeyType = string | number | symbol;

export function RecordWithDefaultValue<K extends t.Mixed, V extends t.Mixed>(
  key: K,
  value: V,
  name = `RecordWithDefaultValue<${key.name}, ${value.name}>`
): RecordWithDefaultValueC<K, V> {
  return t.type(
    {
      _tag: t.literal('RecordWithDefaultValue'),
      record: t.record(key, value),
      defaultValue: value,
    },
    name
  );
}

/**
 * Like a built-in Record type, but with a default value for missing keys.
 */
export interface RecordWithDefaultValue<Key extends KeyType, Value> {
  _tag: 'RecordWithDefaultValue';
  record: Record<Key, Value | undefined>;
  defaultValue: Value;
};

export const createRecord = <K extends KeyType, V>(
  record: Record<K, V>,
  defaultValue: V
): RecordWithDefaultValue<K, V> => {
  return {
    _tag: 'RecordWithDefaultValue',
    record,
    defaultValue,
  };
};

export const getValueFromRecord = <K extends KeyType, V>(key: K, record: RecordWithDefaultValue<K, V>) => {
  return pipe(
    Option.fromNullable(record.record[key]),
    Option.getOrElse(() => getDefaultValueFromRecord(record))
  );
};

export const getDefaultValueFromRecord = <K extends KeyType, V>(record: RecordWithDefaultValue<K, V>) =>
  record.defaultValue;
